import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import Typography from '../../components/Typography';
import Button from '../../components/Button/Button';
import { useEventContext } from '../../context/EventContext';
import HelmetIcon from '../../components/Icons/HelmetIcon';
import UploadFileIcon from '../../components/Icons/UploadFileIcon';
import GroupSolidIcon from '../../components/Icons/GroupSolidIcon';
import DownloadCloudOutlineIcon from '../../components/Icons/DownloadCloudOutlineIcon';
import SearchIcon from '../../components/Icons/SearchIcon';
import { theme } from '../../theme';
import AttendeeEventsDrawer from '../../components/Drawer/AttendeeEventsDrawer';
import axiosClient from '../../api/axiosClient';
import SpinnerCircular from '../../components/SpinnerCircular';
import InputText from '../../components/Input/InputText';
import CloseIcon from '../../components/Icons/CloseIcon';
import useWindowDimensions from '../../hooks/useWindowDimensions';

const MyEventsAttendees = () => {
  const { loading: contextLoading } = useEventContext();
  const [attendees, setAttendees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedAttendee, setSelectedAttendee] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const { width } = useWindowDimensions();
  const isMobile = width < parseInt(theme.colors.breakpoints?.md || '768');
  const { t } = useTranslation('hostedEvents');

  // Función para obtener los asistentes desde el backend
  const fetchAttendees = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axiosClient.get('/api/events/my-attendees');

      if (response.data && response.data.success) {
        setAttendees(response.data.attendees || []);
      } else {
        setError('Error al obtener los asistentes');
        setAttendees([]);
      }
    } catch (err) {
      console.error('Error fetching attendees:', err);
      setError('Error al cargar los asistentes');
      setAttendees([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAttendeeClick = (attendee) => {
    setSelectedAttendee(attendee);
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedAttendee(null);
  };

  // Funciones de navegación entre asistentes
  const handleNextAttendee = () => {
    if (!selectedAttendee || attendees.length === 0) return;

    const currentIndex = attendees.findIndex(attendee => attendee._id === selectedAttendee._id);
    const nextIndex = (currentIndex + 1) % attendees.length; // Loop al principio si estamos en el último
    setSelectedAttendee(attendees[nextIndex]);
  };

  const handlePreviousAttendee = () => {
    if (!selectedAttendee || attendees.length === 0) return;

    const currentIndex = attendees.findIndex(attendee => attendee._id === selectedAttendee._id);
    const previousIndex = currentIndex === 0 ? attendees.length - 1 : currentIndex - 1; // Loop al final si estamos en el primero
    setSelectedAttendee(attendees[previousIndex]);
  };

  // Filtrar asistentes por término de búsqueda
  const filteredAttendees = useMemo(() => {
    if (!searchTerm.trim()) {
      return attendees;
    }

    const searchLower = searchTerm.toLowerCase();
    return attendees.filter(attendee => {
      const fullName = `${attendee.name || ''} ${attendee.lastName || ''}`.toLowerCase();
      const email = (attendee.email || '').toLowerCase();
      return fullName.includes(searchLower) || email.includes(searchLower);
    });
  }, [attendees, searchTerm]);

  // Función para descargar CSV
  const downloadCSV = () => {
    const dataToExport = filteredAttendees.length > 0 ? filteredAttendees : attendees;

    if (dataToExport.length === 0) {
      alert(t('people.noAttendeesToDownload'));
      return;
    }

    // Crear encabezados del CSV
    const headers = [
      t('people.csvHeaders.name'),
      t('people.csvHeaders.lastName'),
      t('people.csvHeaders.email'),
      t('people.csvHeaders.firstEnrollment'),
      t('people.csvHeaders.eventCount')
    ];

    // Convertir datos de asistentes a formato CSV
    const csvData = dataToExport.map(attendee => [
      attendee.name || '',
      attendee.lastName || '',
      attendee.email || '',
      attendee.firstEnrollmentDate
        ? format(new Date(attendee.firstEnrollmentDate), 'dd/MM/yyyy', { locale: es })
        : '',
      attendee.eventsCount || 0
    ]);

    // Combinar encabezados y datos
    const csvContent = [headers, ...csvData]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');

    // Crear y descargar archivo
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `Attendees-${format(new Date(), 'yyyy-MM-dd')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    fetchAttendees();
  }, []);

  return (
    <MyEventsPeopleSection>
      <Container>
        <SectionHeader>
          <HeaderBlock>
            <Typography as="h1" $variant="title-3-semibold" $variantTablet="title-4-semibold" $variantMobile="body-1-semibold">{t('people.title')}</Typography>
            <Typography as="p" $variant="body-1-medium" $variantTablet="body-2-medium" $variantMobile="body-2-medium" color={theme.colors.defaultWeak}>{t('people.description')}</Typography>
          </HeaderBlock>
          {attendees.length > 0 && (
            <Button
              $variant="outline"
              size="m"
              sizeTablet="m"
              sizeMobile="m"
              onClick={downloadCSV}
            >
              <DownloadCloudOutlineIcon fill={theme.colors.defaultStrong} />
              {!isMobile && t('people.downloadCSV')}
            </Button>
          )}
        </SectionHeader>

        {attendees.length > 0 && (
          <SearchContainer>
            <SearchInputWrapper>
              <SearchIconWrapper>
                <SearchIcon fill={theme.colors.defaultWeak} width={16} height={16} />
              </SearchIconWrapper>
              <StyledInputText
                type="text"
                placeholder={t('people.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <ClearButton $iconOnly onClick={() => setSearchTerm('')} $variant="ghost">
                  <CloseIcon fill={theme.colors.defaultStrong} width={16} height={16} />
                </ClearButton>
              )}
            </SearchInputWrapper>
            {searchTerm && (
              <SearchResults>
                <Typography as="span" $variant="body-2-regular" color={theme.colors.defaultWeak}>
                  {t('people.searchResults', { filtered: filteredAttendees.length, total: attendees.length })}
                </Typography>
              </SearchResults>
            )}
          </SearchContainer>
        )}
        {loading ? (
          <LoadingState>
            <Loader>
              <SpinnerCircular size={30} thickness={180} speed={100} color={theme.fill.brandMain} secondaryColor={theme.fill.defaultWeak} />
              <Typography as="p" $variant="body-1-regular" color={theme.colors.defaultWeak}>
                {t('people.loading')}
              </Typography>
            </Loader>
          </LoadingState>
        ) : error ? (
          <ErrorState>
            <Typography as="p" $variant="body-1-regular">{error}</Typography>
            <RetryButton onClick={fetchAttendees}>
              <Typography as="span" $variant="body-2-medium">{t('people.retry')}</Typography>
            </RetryButton>
          </ErrorState>
        ) : attendees.length > 0 ? (
          filteredAttendees.length > 0 ? (
            isMobile ? (
              <CardsContainer>
                {filteredAttendees.map((attendee, index) => (
                  <AttendeeCard
                    key={attendee._id || `attendee-${index}`}
                    onClick={() => handleAttendeeClick(attendee)}
                  >
                    <CardHeader>
                      <AttendeeInfo>
                        <AttendeeAvatar>
                          {attendee.userAvatar ? (
                            <AvatarImage src={attendee.userAvatar} alt={attendee.name} />
                          ) : (
                            <AvatarPlaceholder>
                              <Typography as="span" $variant="body-2-semibold">
                                {attendee.name ? attendee.name.charAt(0).toUpperCase() : '?'}
                              </Typography>
                            </AvatarPlaceholder>
                          )}
                        </AttendeeAvatar>
                        <AttendeeDetails>
                          <Typography as="span" $variant="body-1-semibold" style={{ textDecoration: 'none' }}>
                            {attendee.name} {attendee.lastName}
                          </Typography>
                          <Typography as="span" $variant="body-2-regular" style={{ color: theme.colors.defaultWeak, textDecoration: 'none' }}>
                            {attendee.email || t('people.notAvailable')}
                          </Typography>
                        </AttendeeDetails>
                      </AttendeeInfo>
                    </CardHeader>
                    <CardFooter>
                      <Typography as="span" $variant="body-2-regular" style={{ color: theme.colors.defaultWeak }}>
                        {t('people.firstEnrollment')}: {attendee.firstEnrollmentDate
                          ? format(new Date(attendee.firstEnrollmentDate), 'dd MMM yyyy', { locale: es })
                          : t('people.notAvailable')
                        }
                      </Typography>
                      <EventsCount>
                        <Typography as="span" $variant="body-2-medium">
                          {t('people.eventCount', { count: attendee.eventsCount || 0 })}
                        </Typography>
                      </EventsCount>
                    </CardFooter>
                  </AttendeeCard>
                ))}
              </CardsContainer>
            ) : (
              <TableContainer>
                <AttendeesTable>
                  <TableHeader>
                    <TableRow>
                      <TableHeaderCell>
                        <Typography as="span" $variant="body-2-semibold">
                          {t('people.tableHeaders.attendee')}
                        </Typography>
                      </TableHeaderCell>
                      <TableHeaderCell>
                        <Typography as="span" $variant="body-2-semibold">
                          {t('people.tableHeaders.email')}
                        </Typography>
                      </TableHeaderCell>
                      <TableHeaderCell>
                        <Typography as="span" $variant="body-2-semibold">
                          {t('people.tableHeaders.firstEnrollment')}
                        </Typography>
                      </TableHeaderCell>
                      <TableHeaderCell>
                        <Typography as="span" $variant="body-2-semibold">
                          {t('people.tableHeaders.events')}
                        </Typography>
                      </TableHeaderCell>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredAttendees.map((attendee, index) => (
                      <TableRow
                        key={attendee._id || `attendee-${index}`}
                        onClick={() => handleAttendeeClick(attendee)}
                        $clickable
                      >
                        <TableCell>
                          <AttendeeInfo>
                            <AttendeeAvatar>
                              {attendee.userAvatar ? (
                                <AvatarImage src={attendee.userAvatar} alt={attendee.name} />
                              ) : (
                                <AvatarPlaceholder>
                                  <Typography as="span" $variant="body-2-semibold">
                                    {attendee.name ? attendee.name.charAt(0).toUpperCase() : '?'}
                                  </Typography>
                                </AvatarPlaceholder>
                              )}
                            </AttendeeAvatar>
                            <AttendeeDetails>
                              <Typography as="span" $variant="body-1-semibold" style={{ textDecoration: 'none' }}>
                                {attendee.name} {attendee.lastName}
                              </Typography>
                            </AttendeeDetails>
                          </AttendeeInfo>
                        </TableCell>
                        <TableCell>
                          <Typography as="span" $variant="body-2-regular" style={{ textDecoration: 'none' }}>
                            {attendee.email || t('people.notAvailable')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography as="span" $variant="body-2-regular">
                            {attendee.firstEnrollmentDate
                              ? format(new Date(attendee.firstEnrollmentDate), 'dd MMM yyyy', { locale: es })
                              : t('people.notAvailable')
                            }
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <EventsCount>
                            <Typography as="span" $variant="body-2-medium">
                              {t('people.eventCount', { count: attendee.eventsCount || 0 })}
                            </Typography>
                          </EventsCount>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </AttendeesTable>
              </TableContainer>
            )
          ) : (
            <NoResultsState>
              <EmptyStateIcon>
                <SearchIcon fill={theme.colors.defaultStrong} width={24} height={24} />
              </EmptyStateIcon>
              <Typography as="p" $variant="body-1-medium" color={theme.colors.defaultStrong} $align="center">
                {t('people.noResults.title')}
              </Typography>
              <Typography as="p" $variant="body-2-medium" color={theme.colors.defaultWeak} $align="center">
                {t('people.noResults.description')}
              </Typography>
              <ClearSearchButton onClick={() => setSearchTerm('')} $variant="ghost">
                {t('people.noResults.clearSearch')}
              </ClearSearchButton>
            </NoResultsState>
          )
        ) : (
          <EmptyState>
            <EmptyStateIcon>
              <GroupSolidIcon fill={theme.colors.defaultStrong} />
            </EmptyStateIcon>
            <Typography as="p" $variant="body-1-medium" color={theme.colors.defaultStrong} $align="center">
              {t('people.emptyState.title')}
            </Typography>
            <Typography as="p" $variant="body-2-medium" color={theme.colors.defaultWeak} $align="center">
              {t('people.emptyState.description')}
            </Typography>
          </EmptyState>
        )}

        <AttendeeEventsDrawer
          isOpen={isDrawerOpen}
          onClose={handleCloseDrawer}
          attendeeId={selectedAttendee?._id}
          attendeeName={selectedAttendee ? `${selectedAttendee.name} ${selectedAttendee.lastName}` : ''}
          attendeeEmail={selectedAttendee?.email}
          attendeeAvatar={selectedAttendee?.userAvatar}
          anchor="right"
          onNextAttendee={handleNextAttendee}
          onPreviousAttendee={handlePreviousAttendee}
          hasMultipleAttendees={filteredAttendees.length > 1}
          width={500}
        />

      </Container>
    </MyEventsPeopleSection>
  );
};

export default MyEventsAttendees;
const MyEventsPeopleSection = styled.section`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: ${({ theme }) => theme.fill.defaultMain};
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.sizing.md} ${({ theme }) => theme.sizing.md};
  width: 100%;
  max-width: 1400px;
  gap: ${({ theme }) => theme.sizing.md};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.sizing.sm};
    padding-bottom: 0px;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${({ theme }) => theme.sizing.md};
  
  // @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
  //   flex-direction: column;
  //   align-items: stretch;
  //   gap: ${({ theme }) => theme.sizing.sm};
  // }
`;

// const SectionTitle = styled.div`
//   display: flex;
//   flex-direction: column;
//   gap: ${({ theme }) => theme.sizing.xs};
//   flex: 1;
// `;

const TableContainer = styled.div`
  background-color: ${({ theme }) => theme.fill.defaultMain};
  border: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  border-radius: ${({ theme }) => theme.radius.xs};
  overflow: hidden;
  box-shadow: 0 0 2px -1px ${({ theme }) => theme.fill.inverseAlphaMain16};
`;

const AttendeesTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead``;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  border-bottom: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  transition: background-color 0.2s ease;
  background-color: transparent;
  
  &:last-child {
    border-bottom: none;
  }
  
  ${({ $clickable, theme }) => $clickable && css`
    cursor: pointer;
    
    &:hover {
      background-color: ${theme.fill.defaultSubtle};
    }
  `}
`;

const TableHeaderCell = styled.th`
  text-align: left;
  padding: ${({ theme }) => theme.sizing.xs} ${({ theme }) => theme.sizing.sm};
  // background-color: ${({ theme }) => theme.fill.defaultWeak};
  border-bottom: 1px solid ${({ theme }) => theme.border.defaultSubtle};
`;

const TableCell = styled.td`
  padding: ${({ theme }) => theme.sizing.sm} ${({ theme }) => theme.sizing.sm};
  vertical-align: middle;
`;

const AttendeeAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
`;

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const AvatarPlaceholder = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  background-color: ${({ theme }) => theme.fill.defaultWeak};
  color: ${({ theme }) => theme.colors.defaultStrong};
`;

const AttendeeInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.sizing.sm};
`;

const AttendeeDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  
  /* Prevent Chrome from auto-detecting and underlining email addresses */
  * {
    text-decoration: none !important;
  }
`;

const EventsCount = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  background-color: ${({ theme }) => theme.fill.defaultWeak};
  border-radius: ${({ theme }) => theme.radius.xs};
  padding: 0 ${({ theme }) => theme.sizing.xs};
  white-space: nowrap;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: ${({ theme }) => theme.sizing.xs};
  // padding: ${({ theme }) => theme.sizing.lg};
  border-radius: ${({ theme }) => theme.radius.xs};
  border: 1px solid ${({ theme }) => theme.border.defaultWeak};
  padding: ${({ theme }) => theme.sizing.lg} ${({ theme }) => theme.sizing.sm};
  width: 100%;
`;

const EmptyStateIcon = styled.div`
  width: ${({ theme }) => theme.sizing.xxxl};
  height: ${({ theme }) => theme.sizing.xxxl};
  border-radius: ${({ theme }) => theme.radius.xs};
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${({ theme }) => theme.fill.defaultWeak};
`;

const LoadingState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: ${({ theme }) => theme.radius.xs};
  border: 1px solid ${({ theme }) => theme.border.defaultWeak};
  padding: ${({ theme }) => theme.sizing.md} ${({ theme }) => theme.sizing.sm};
  width: 100%;
`;

const Loader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.sizing.xs};
`;

const ErrorState = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: ${({ theme }) => theme.sizing.lg};
  gap: ${({ theme }) => theme.sizing.sm};
  color: ${({ theme }) => theme.colors.defaultWeak};
`;

const RetryButton = styled.button`
  padding: ${({ theme }) => theme.sizing.xs} ${({ theme }) => theme.sizing.sm};
  background-color: ${({ theme }) => theme.fill.defaultMain};
  border: 1px solid ${({ theme }) => theme.border.defaultMain};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${({ theme }) => theme.fill.defaultWeak};
    border-color: ${({ theme }) => theme.border.defaultStrong};
  }
  
  &:active {
    transform: translateY(1px);
  }
`;

const SearchContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.xs};
`;

const SearchInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const StyledInputText = styled(InputText)`
  padding-left: 40px;
  padding-right: ${({ searchTerm }) => searchTerm ? '40px' : '16px'};
`;

const SearchIconWrapper = styled.div`
  position: absolute;
  left: 12px;
  display: flex;
  align-items: center;
  pointer-events: none;
  z-index: 1;
`;



const ClearButton = styled(Button)`
  position: absolute;
  right: ${({ theme }) => theme.sizing.xxs};
`;

const SearchResults = styled.div`
  display: flex;
  align-items: center;
  padding-left: ${({ theme }) => theme.sizing.xs};
`;

const NoResultsState = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: ${({ theme }) => theme.sizing.xs};
  border-radius: ${({ theme }) => theme.radius.xs};
  border: 1px solid ${({ theme }) => theme.border.defaultWeak};
  padding: ${({ theme }) => theme.sizing.lg} ${({ theme }) => theme.sizing.sm};
  width: 100%;
`;

const ClearSearchButton = styled(Button)`
  margin-top: ${({ theme }) => theme.sizing.xs};
`;

const HeaderBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.xxs};
`;

const CardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.xs};
`;

const AttendeeCard = styled.div`
  background-color: ${({ theme }) => theme.fill.defaultMain};
  border: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  border-radius: ${({ theme }) => theme.radius.xs};
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: ${({ theme }) => theme.fill.defaultSubtle};
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: ${({ theme }) => theme.sizing.xs};
  // margin-bottom: ${({ theme }) => theme.sizing.xs};
`;

const CardFooter = styled.div`
  // margin-top: ${({ theme }) => theme.sizing.xs};
  border-top: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.sizing.xs};
`;

// PropTypes
MyEventsAttendees.propTypes = {
  // Este componente no recibe props directamente
};